import React, { useState, useMemo } from 'react';
import { Search, Crown, Camera, CheckCircle, Clock, Ban, MapPin, Star, Briefcase } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { resolveAssetUrl } from '../services/api';

/**
 * DriverPhotoWall — admin-only gallery of driver profile photos for rapid
 * visual moderation. Renders a responsive grid (2–8 columns depending on
 * viewport) of 150×150-ish cards: photo on top, name + premium badge below,
 * status + counties beneath. Filters/sort/search/pagination are all
 * client-side over the existing /admin/users response so we don't need a
 * new backend endpoint.
 *
 * Props:
 *   users: array of user rows from getAllUsers() (only `role==='driver'`
 *          rows are rendered; mixed input is filtered automatically)
 *   onOpenDriver(driverId): called when a card is clicked — main dashboard
 *          opens the existing driver-details surface (same as the list view).
 */
const PAGE_SIZE = 32;

const STATUS_META = {
  active:         { label: 'Active',         className: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  pending_review: { label: 'Pending review', className: 'bg-amber-100 text-amber-800 border-amber-200',    Icon: Clock },
  banned:         { label: 'Banned',         className: 'bg-red-100 text-red-700 border-red-200',         Icon: Ban },
};

export default function DriverPhotoWall({ users = [], onOpenDriver }) {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | premium | active | pending_review | no_photo
  const [sortMode, setSortMode] = useState('newest');   // newest | oldest | jobs | rating | last_active
  const [page, setPage] = useState(1);

  // Pure-derive the visible list from props so a parent refresh re-renders.
  const visible = useMemo(() => {
    let rows = (users || []).filter(u => u.role === 'driver');

    // Status / category filter
    if (filterMode === 'premium')        rows = rows.filter(u => u.is_premium);
    else if (filterMode === 'active')    rows = rows.filter(u => !u.banned && u.compliance_status === 'active');
    else if (filterMode === 'pending_review') rows = rows.filter(u => u.compliance_status === 'pending_review');
    else if (filterMode === 'no_photo')  rows = rows.filter(u => !u.profile_image);

    // Search across name / email / phone
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(u =>
        (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.phone || '').toLowerCase().includes(q)
      );
    }

    // Sort
    const ts = (x) => x ? new Date(x).getTime() || 0 : 0;
    if      (sortMode === 'oldest')   rows.sort((a, b) => ts(a.created_at) - ts(b.created_at));
    else if (sortMode === 'jobs')     rows.sort((a, b) => (b.jobs_completed || 0) - (a.jobs_completed || 0));
    else if (sortMode === 'rating')   rows.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
    else if (sortMode === 'last_active') rows.sort((a, b) => ts(b.last_active || b.created_at) - ts(a.last_active || a.created_at));
    else /* newest */                 rows.sort((a, b) => ts(b.created_at) - ts(a.created_at));

    return rows;
  }, [users, filterMode, sortMode, search]);

  // Reset to page 1 when filters change (keeps state simple)
  React.useEffect(() => { setPage(1); }, [filterMode, sortMode, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const FilterChip = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setFilterMode(value)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        filterMode === value
          ? 'bg-[#2E7D32] text-white border-[#2E7D32]'
          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
      }`}
      data-testid={`photo-wall-filter-${value}`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="driver-photo-wall">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search name, email or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="photo-wall-search"
            />
          </div>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            className="border border-gray-200 rounded-md px-2 h-9 text-xs bg-white"
            data-testid="photo-wall-sort"
          >
            <option value="newest">Newest joined</option>
            <option value="oldest">Oldest joined</option>
            <option value="jobs">Most jobs completed</option>
            <option value="rating">Highest rated</option>
            <option value="last_active">Last active</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="photo-wall-filters">
          <FilterChip value="all"            label="All drivers" />
          <FilterChip value="premium"        label="⭐ Premium" />
          <FilterChip value="active"         label="Active" />
          <FilterChip value="pending_review" label="Pending review" />
          <FilterChip value="no_photo"       label="No profile photo" />
        </div>
        <p className="text-xs text-gray-500" data-testid="photo-wall-count">
          Showing <span className="font-semibold text-gray-800">{pageRows.length}</span> of{' '}
          <span className="font-semibold text-gray-800">{visible.length}</span> drivers
        </p>
      </div>

      {/* Grid */}
      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-500" data-testid="photo-wall-empty">
          No drivers match your filters.
        </div>
      ) : (
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3"
          data-testid="photo-wall-grid"
        >
          {pageRows.map((d) => (
            <PhotoCard key={d.id} driver={d} onClick={() => onOpenDriver && onOpenDriver(d.id)} />
          ))}
        </ul>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 text-xs" data-testid="photo-wall-pagination">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            data-testid="photo-wall-prev"
          >Prev</Button>
          <span className="px-2 text-gray-600">Page {page} of {totalPages}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            data-testid="photo-wall-next"
          >Next</Button>
        </div>
      )}
    </div>
  );
}

function PhotoCard({ driver, onClick }) {
  const status = driver.banned ? 'banned' : (driver.compliance_status || 'pending_review');
  const meta = STATUS_META[status] || STATUS_META.pending_review;
  const StatusIcon = meta.Icon;
  const photoUrl = driver.profile_image ? resolveAssetUrl(driver.profile_image) : null;
  // County summary — "All Ireland" / "Dublin · Cork (+2 more)" / "—"
  const counties = driver.serves_all_ireland
    ? 'All Ireland'
    : Array.isArray(driver.counties) && driver.counties.length
      ? (driver.counties.length > 2
          ? `${driver.counties.slice(0, 2).join(' · ')} (+${driver.counties.length - 2})`
          : driver.counties.join(' · '))
      : '—';

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#2E7D32] hover:shadow-md transition-all group"
        data-testid={`photo-wall-card-${driver.id}`}
      >
        {/* Square photo / placeholder */}
        <div className="relative aspect-square bg-gray-100 overflow-hidden">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={driver.name || 'Driver'}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
              data-testid={`photo-wall-img-${driver.id}`}
            />
          ) : null}
          {/* Placeholder fallback (also shown when img fails to load) */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50 to-amber-50 text-gray-400"
            style={{ display: photoUrl ? 'none' : 'flex' }}
            data-testid={`photo-wall-placeholder-${driver.id}`}
          >
            <Camera className="w-9 h-9 mb-1.5" strokeWidth={1.25} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">No photo</span>
          </div>
          {driver.is_premium && (
            <span
              className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold ring-2 ring-white"
              title="Premium driver"
              data-testid={`photo-wall-premium-${driver.id}`}
            >
              <Crown className="w-2.5 h-2.5" />
              Premium
            </span>
          )}
          <span
            className={`absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${meta.className} ring-1 ring-white/60`}
            data-testid={`photo-wall-status-${driver.id}`}
          >
            <StatusIcon className="w-2.5 h-2.5" />
            {meta.label}
          </span>
        </div>

        {/* Caption */}
        <div className="p-2.5">
          <p className="text-sm font-bold text-gray-900 truncate" data-testid={`photo-wall-name-${driver.id}`}>
            {driver.name || '—'}
          </p>
          <p className="mt-0.5 text-[11px] text-gray-500 flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{counties}</span>
          </p>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
            <span className="inline-flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5 text-amber-500" />
              {driver.avg_rating ? driver.avg_rating.toFixed(1) : '—'}
            </span>
            <span className="inline-flex items-center gap-0.5">
              <Briefcase className="w-2.5 h-2.5" />
              {driver.jobs_completed || 0}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}
